import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

const goldGradient = "linear-gradient(135deg, #d4af37 0%, #b17d2a 100%)";
const glassBackground = "rgba(255, 255, 255, 0.7)";

export const HomeContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  background: radial-gradient(circle at top right, #ffffff 0%, #f4f7f6 100%);
  font-family: "Inter", sans-serif;

  @media (min-width: 768px) {
    padding: 40px;
  }
`;

export const DashboardContainer = styled.div`
  width: 100%;
  max-width: 1240px;
  margin-bottom: 30px;
  padding: 20px;
  background: ${glassBackground};
  backdrop-filter: blur(15px);
  border: 1px solid rgba(177, 125, 42, 0.1);
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
  animation: ${fadeIn} 0.6s ease-out;

  @media (min-width: 768px) {
    padding: 30px;
    margin-bottom: 40px;
  }
`;

export const DashboardHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 20px;

  h2 {
    font-size: 20px;
    background: ${goldGradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0;
  }

  .header-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    width: 100%;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    h2 {
      font-size: 24px;
    }
    .header-actions {
      flex-direction: row;
      width: auto;
      gap: 20px;
    }
  }
`;

export const SupportButton = styled.button`
  background: white;
  border: 1px solid #b17d2a;
  color: #b17d2a;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: ${goldGradient};
    color: white;
    transform: translateY(-2px);
  }

  @media (min-width: 768px) {
    width: auto;
    font-size: 14px;
  }
`;

export const MonthNavigator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
  padding: 8px 15px;
  border-radius: 50px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  width: 100%;
  max-width: 300px;

  span {
    font-size: 14px;
    color: #333;
    font-weight: 600;
    flex: 1;
    text-align: center;
    white-space: nowrap;
  }

  button {
    background: transparent;
    border: none;
    color: #b17d2a;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  @media (min-width: 768px) {
    width: auto;
    span {
      font-size: 15px;
      min-width: 140px;
    }
  }
`;

export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 15px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

export const StatCard = styled.div`
  background: white;
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${(props) => props.color || "#b17d2a"};
  }

  .icon {
    font-size: 20px;
    color: ${(props) => props.color || "#b17d2a"};
    margin-bottom: 10px;
  }

  .label {
    font-size: 11px;
    color: #888;
    text-transform: uppercase;
    font-weight: 700;
  }

  .value {
    font-size: 16px;
    color: #222;
    font-weight: 800;
    margin-top: 5px;
    word-break: break-all;
  }

  &.skeleton {
    height: 80px;
    background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.5s infinite linear;
  }
`;

export const HomeGrid = styled.div`
  width: 100%;
  max-width: 1240px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  padding-bottom: 40px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const OptionCard = styled.div`
  min-height: 100px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  padding: 15px 20px;
  gap: 15px;
  background: white;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 768px) {
    height: 120px;
    padding: 0 25px;
    gap: 20px;
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 30px rgba(177, 125, 42, 0.15);
    }
  }

  .iconBox {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f9f9f9;
    border-radius: 12px;

    img {
      width: 28px;
      height: 28px;
    }
  }

  .name {
    font-size: 16px;
    color: #444;
    font-weight: 700;
  }

  @media (min-width: 768px) {
    .iconBox {
      width: 60px;
      height: 60px;
      img {
        width: 32px;
        height: 32px;
      }
    }
    .name {
      font-size: 20px;
    }
  }
`;

export const SubMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const SubMenuContainer = styled.div`
  width: 100%;
  max-width: 450px;
  background: white;
  padding: 30px 20px;
  border-radius: 24px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-height: 80vh;
  overflow-y: auto;

  h2 {
    color: #333;
    font-size: 22px;
    margin-bottom: 20px;
    font-weight: 800;
  }

  @media (min-width: 768px) {
    padding: 40px;
    border-radius: 30px;
    h2 {
      font-size: 28px;
    }
  }
`;

export const SubMenuOption = styled.div`
  padding: 14px;
  margin-bottom: 10px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  background: #fdfdfd;
  border: 1px solid #f0f0f0;
  transition: 0.2s;

  &:hover {
    background: ${goldGradient};
    color: white;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #fff;
  border: none;
  color: #b17d2a;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    top: 30px;
    right: 30px;
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

export const ErrorState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  color: #ff4d4d;
  font-weight: 600;
`;
